import React from "react";
import { CrossIcon } from "@100mslive/react-icons";
import { Box, Dialog, Flex, Text } from "@100mslive/react-ui";
import { HANDOFF_WAIT_SECONDS } from "../common/constants";

// This modal deliberately opts out of the app's dark theme — it is a warm/cream
// surface per the design mock, so colours are literal rather than theme tokens.
const PALETTE = {
  surface: "#FDF8EF",
  ink: "#14251C",
  green: "#1D5B39",
  greenButton: "#2C6446",
  red: "#A5432F",
  muted: "#5A6560",
  divider: "#E5DCCB",
  statsTray: "#E7EDE4",
  cardGood: "#F8FAF7",
  cardGoodBorder: "#E2E9DF",
  cardBad: "#EFE8DF",
  cardBadBorder: "#E4D9C9",
  closeButton: "#EAE4DA",
  countdownBg: "#FFFDF8",
  countdownBorder: "#EAE1D2",
  progressTrack: "#EDE6DA",
};

const SERIF = "Georgia, 'Times New Roman', serif";

// The modal never scrolls — a scrollbar pushes the actions below the fold, which is
// the one thing that must always be reachable. Instead every dimension scales with
// the viewport so the whole thing shrinks to fit a short screen. Each clamp is
// min / preferred / max, and the vertical ones track vh so height compresses first.
// Type scales on min(vw, vh) so a short-but-wide window compresses too — sizing on
// vw alone would keep the text large and push the actions off a laptop in a small
// window. Spacing is vh-driven for the same reason. At the small end of every clamp
// the panel is ~400px tall, so it fits any real viewport without a scrollbar.
const SIZE = {
  panelPadding: "clamp(1.25rem, min(3.5vw, 4vh), 2.5rem)",
  heading: "clamp(1.6rem, min(4.5vw, 5vh), 2.75rem)",
  percent: "clamp(1.6rem, min(4.5vw, 5vh), 2.75rem)",
  countdown: "clamp(1.4rem, min(4vw, 4.5vh), 2.5rem)",
  bodyLead: "clamp(0.95rem, min(1.6vw, 2vh), 1.125rem)",
  body: "clamp(0.85rem, min(1.4vw, 1.8vh), 1rem)",
  cardPadding: "clamp(0.65rem, min(1.8vw, 2vh), 1.25rem)",
  gapSm: "clamp(0.35rem, 1vh, 0.75rem)",
  gapMd: "clamp(0.6rem, 2vh, 1.5rem)",
  gapLg: "clamp(0.85rem, 2.5vh, 2rem)",
  closeButton: "clamp(2.25rem, min(5vw, 5.5vh), 3rem)",
};

// Screening calls and first online follow-ups are different pitches to the provider —
// an SC sells same-day conversion, an FU sells Rx purchase — so only the numbers and
// the copy vary. The layout and the actions are identical for both.
export const HANDOFF_VARIANTS = {
  SCREENING_CALL: "screening_call",
  FIRST_ONLINE_FU: "first_online_fu",
};

const COPY = {
  [HANDOFF_VARIANTS.SCREENING_CALL]: {
    title: "Complete Screening Call Handoff",
    good: {
      percent: "35%",
      label: "Successful handoff",
      description: "same-day Rx conversion",
      footnote:
        "Higher screening-call conversion means more repeat consultations.",
    },
    bad: {
      percent: "25%",
      label: "Without handoff",
      description: "same-day Rx conversion",
    },
    lead: {
      start:
        "This patient is new to online consultation. Submit the Rx so the care team can join and guide the patient through the next steps.",
      waiting: `The care team has been alerted to join this patient's first online screening call. Please stay on the call for the ${HANDOFF_WAIT_SECONDS}-second handoff window.`,
    },
  },
  [HANDOFF_VARIANTS.FIRST_ONLINE_FU]: {
    title: "Complete Rx Handshake Alert",
    good: {
      percent: "60%",
      label: "Successful handoff",
      description: "6 out of 10 patients purchase the Rx.",
    },
    bad: {
      percent: "30%",
      label: "Without handoff",
      description: "3 out of 10 patients purchase the Rx.",
    },
    lead: {
      start: `In more than 90% of cases, the care team joins within ${HANDOFF_WAIT_SECONDS} seconds.`,
      waiting: `In more than 90% of cases, the care team joins within ${HANDOFF_WAIT_SECONDS} seconds.`,
    },
  },
};

const StatCard = ({
  percent,
  percentColor,
  background,
  borderColor,
  label,
  description,
  footnote,
}) => (
  <Box
    css={{
      flex: "1 1 0",
      minWidth: 0,
      padding: SIZE.cardPadding,
      borderRadius: "0.75rem",
      backgroundColor: background,
      border: `1px solid ${borderColor}`,
    }}
  >
    <Text
      css={{
        fontSize: SIZE.percent,
        lineHeight: 1,
        fontWeight: 700,
        color: percentColor,
      }}
    >
      {percent}
    </Text>
    <Text
      css={{
        fontSize: SIZE.bodyLead,
        fontWeight: 700,
        color: PALETTE.green,
        marginTop: SIZE.gapSm,
      }}
    >
      {label}
    </Text>
    <Text
      css={{
        fontSize: SIZE.body,
        lineHeight: 1.4,
        color: PALETTE.muted,
        marginTop: SIZE.gapSm,
      }}
    >
      {description}
    </Text>
    {footnote && (
      <Text
        css={{
          fontSize: SIZE.body,
          lineHeight: 1.4,
          color: PALETTE.muted,
          marginTop: SIZE.gapMd,
        }}
      >
        {footnote}
      </Text>
    )}
  </Box>
);

/**
 * Shown to a provider leaving a call before a care team agent has joined.
 * stage "start" -> Rx not submitted yet, sends the provider back to submit it
 * stage "waiting" -> Rx submitted, counts down while waiting for the agent to join
 *
 * Both stages resolve to "keep the provider on the call", so the primary action is
 * the same callback; only the label and the countdown differ.
 */
export const HandoffAlertModal = ({
  open,
  stage,
  variant = HANDOFF_VARIANTS.FIRST_ONLINE_FU,
  secondsRemaining = HANDOFF_WAIT_SECONDS,
  onStayOnCall,
  onLeaveAnyway,
  onClose,
}) => {
  const isWaiting = stage === "waiting";
  const copy = COPY[variant] || COPY[HANDOFF_VARIANTS.FIRST_ONLINE_FU];
  const progressPct = Math.max(
    0,
    Math.min(100, (secondsRemaining / HANDOFF_WAIT_SECONDS) * 100)
  );

  return (
    <Dialog.Root open={open} onOpenChange={value => !value && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay />
        <Dialog.Content
          css={{
            width: "min(40rem, 92vw)",
            padding: SIZE.panelPadding,
            borderRadius: "1.5rem",
            backgroundColor: PALETTE.surface,
            color: PALETTE.ink,
            border: "none",
          }}
        >
          <Flex justify="between" align="start" css={{ gap: SIZE.gapMd }}>
            <Text
              css={{
                fontFamily: SERIF,
                fontSize: SIZE.heading,
                lineHeight: 1.05,
                fontWeight: 700,
                color: PALETTE.ink,
                maxWidth: "70%",
              }}
            >
              {copy.title}
            </Text>
            <Box
              as="button"
              onClick={onClose}
              data-testid="handoff_alert_close"
              css={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                width: SIZE.closeButton,
                height: SIZE.closeButton,
                borderRadius: "50%",
                border: "none",
                cursor: "pointer",
                fontFamily: "inherit",
                color: PALETTE.ink,
                backgroundColor: PALETTE.closeButton,
              }}
            >
              <CrossIcon />
            </Box>
          </Flex>

          <Box
            css={{
              height: "1px",
              backgroundColor: PALETTE.divider,
              margin: `${SIZE.gapMd} 0`,
            }}
          />

          <Flex
            css={{
              gap: SIZE.gapMd,
              padding: SIZE.gapMd,
              borderRadius: "1rem",
              backgroundColor: PALETTE.statsTray,
              "@md": { flexDirection: "column" },
            }}
          >
            <StatCard
              percentColor={PALETTE.green}
              background={PALETTE.cardGood}
              borderColor={PALETTE.cardGoodBorder}
              {...copy.good}
            />
            <StatCard
              percentColor={PALETTE.red}
              background={PALETTE.cardBad}
              borderColor={PALETTE.cardBadBorder}
              {...copy.bad}
            />
          </Flex>

          <Text
            css={{
              fontSize: SIZE.bodyLead,
              fontWeight: 700,
              color: PALETTE.green,
              marginTop: SIZE.gapMd,
            }}
          >
            {isWaiting ? copy.lead.waiting : copy.lead.start}
          </Text>

          {isWaiting && (
            <Box
              css={{
                marginTop: SIZE.gapMd,
                padding: SIZE.cardPadding,
                borderRadius: "0.875rem",
                backgroundColor: PALETTE.countdownBg,
                border: `1px solid ${PALETTE.countdownBorder}`,
              }}
            >
              <Flex justify="between" align="center" css={{ gap: SIZE.gapMd }}>
                <Box>
                  <Text
                    css={{
                      fontSize: SIZE.bodyLead,
                      fontWeight: 700,
                      color: PALETTE.ink,
                    }}
                  >
                    Care team joining window
                  </Text>
                  <Text
                    css={{
                      fontSize: SIZE.body,
                      color: PALETTE.muted,
                      marginTop: SIZE.gapSm,
                    }}
                  >
                    Please stay on call for this countdown.
                  </Text>
                </Box>
                <Text
                  css={{
                    flexShrink: 0,
                    fontSize: SIZE.countdown,
                    lineHeight: 1,
                    fontWeight: 700,
                    color: PALETTE.green,
                  }}
                >
                  {secondsRemaining}s
                </Text>
              </Flex>
              <Box
                css={{
                  marginTop: SIZE.gapMd,
                  height: "0.5rem",
                  borderRadius: "999px",
                  backgroundColor: PALETTE.progressTrack,
                  overflow: "hidden",
                }}
              >
                <Box
                  css={{
                    height: "100%",
                    width: `${progressPct}%`,
                    borderRadius: "999px",
                    backgroundImage:
                      "linear-gradient(90deg, #2E7D4F 0%, #C9A227 100%)",
                    transition: "width 1s linear",
                  }}
                />
              </Box>
            </Box>
          )}

          {isWaiting && (
            <Text
              css={{
                fontSize: SIZE.body,
                lineHeight: 1.5,
                color: PALETTE.muted,
                marginTop: SIZE.gapMd,
              }}
            >
              <Text as="span" css={{ fontWeight: 700, color: PALETTE.ink }}>
                Note:{" "}
              </Text>
              If you leave, others can keep going without you. You can still
              rejoin.
            </Text>
          )}

          <Flex
            justify="end"
            align="center"
            css={{ marginTop: SIZE.gapMd, gap: SIZE.gapLg, flexWrap: "wrap" }}
          >
            <Box
              as="button"
              onClick={onLeaveAnyway}
              data-testid="handoff_alert_leave_anyway"
              css={{
                padding: `${SIZE.gapSm} ${SIZE.gapLg}`,
                borderRadius: "999px",
                border: "none",
                cursor: "pointer",
                fontFamily: "inherit",
                fontSize: SIZE.bodyLead,
                fontWeight: 700,
                color: "#FFFFFF",
                backgroundColor: PALETTE.red,
              }}
            >
              Leave Anyway
            </Box>
            <Box
              as="button"
              onClick={onStayOnCall}
              data-testid="handoff_alert_primary_action"
              css={{
                padding: `${SIZE.gapSm} ${SIZE.gapLg}`,
                borderRadius: "999px",
                border: "none",
                cursor: "pointer",
                fontFamily: "inherit",
                fontSize: SIZE.bodyLead,
                fontWeight: 700,
                color: "#FFFFFF",
                backgroundColor: PALETTE.greenButton,
              }}
            >
              {isWaiting ? "Stay on Call" : "Submit Rx & Start Handoff"}
            </Box>
          </Flex>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
